import { Injectable } from '@angular/core';


@Injectable({
  providedIn: 'root'
})
export class LogicParserService {

    constructor() { }

    extractVariables(expression: string): string[] {
        //cleaning up expression to ensure tokens are exectracted correctly
        if (expression?.length > 0) {
            //change ('08',  '10') to ('08','10')
            expression = expression.trim().replace(/,\s+/,',');
        }
        
        const operators = ['AND', 'OR', 'NOT', 'IN', 'NOT IN', '=', '!='];
        const parentheses = ['(', ')'];
        const valuesRegex = /'[^']*'/g; // Match single-quoted strings
        const tokens = expression.split(/\s+/);
        const variables: string[] = [];

        tokens.forEach(token => {
            token = token.replaceAll('(', '').replaceAll(')', '');  
            if ((token != '') && !operators.includes(token) && !parentheses.includes(token) && !valuesRegex.test(token)) {
                variables.push(token);
            }
        });
        return Array.from(new Set(variables)); // Remove duplicates
    }

    evaluateBooleanExpression(expression: string): boolean {
        //function with boolean parameters and return type
        //performance after NOT IN / IN have been evaluated
        const operators = {
            'AND': (a: boolean, b: boolean):boolean => a && b,
            'OR': (a: boolean, b: boolean):boolean => a || b,
            //'NOT': (a: boolean):boolean => !a
        };

        //evaluate does 1 boolean clause 
        function evaluate(tokens: string[]) : boolean {
            const stack: (boolean | string)[] = [];  //store originally as string and then update to boolean
            //every stack entry should start as a string and then be converted to boolean
            let clauseStr = '';
            for (let i = 0; i < tokens.length; i++) {
                //no AND, OR, NOT
                if (tokens[i] in operators) {
                    stack.push(tokens[i]);

                } else if (tokens[i] === '(' || tokens[i] === ')') {
                    //has parentheses - some for grouping, others for sets
                    //will not see parenthesis for NOT IN or IN
                    stack.push(tokens[i]);
                } else if (tokens[i].includes('IN') || tokens[i].includes('NOT IN')) {
                    //works only for NOT IN
                    //get the value, operator, and values
                    //is there is a performance impact in this try/catch block?
                    try {
                        let clauseValue = tokens[i-1].replace(/'/g, '') as string;
                        let clauseOperator = tokens[i];
                        let clauseValues = [] as string[]; //create a method that gets the values
                        //advance to next ")" and take previous values, except for "("
                        while (tokens[i] !== ')') {
                            i++;
                            if (tokens[i] !== '(') {
                                let value = tokens[i].replace(/'/g, '')
                                clauseValues.push(value);
                            }
                        }

                        //console.log('evaluate', 'clauseValue', clauseValue, 'clauseOperator', clauseOperator, 'clauseValues', clauseValues);
                        if (clauseValue.toUpperCase() == 'ANY') {
                            //console.log('evaluate ANY', 'clauseValue', clauseValue, 'clauseOperator', clauseOperator, 'clauseValues', clauseValues);

                            stack.push(true); //if ANY, then always true
                        } else {
                            let result: boolean;
                            switch (clauseOperator) {
                                case 'IN':
                                    result = clauseValues.includes(clauseValue);
                                    break;
                                case 'NOT IN':
                                    result = !clauseValues.includes(clauseValue);
                                    break;
                                default:
                                    throw new Error(`Unknown operator: ${clauseOperator}`);
                            }
                            stack.push(result);
                        }
    
                    } catch (error) {
                        console.error('evaluate', 'error', tokens, error, i, tokens[i]);
                    }
                }
            }
    
            //should have operators surrounded by boolean values
            let finalResult = evaluateReducedExpression(stack);

            return finalResult;
        }

        function evaluateReducedExpression(tokens: (boolean | string)[]): boolean {

            //console.log('evaluateReducedExpression', 'start', tokens);

            const operators = {
                'AND': (a: boolean, b: boolean) => a && b,
                'OR': (a: boolean, b: boolean) => a || b,
                'NOT': (a: boolean) => !a
            };
        
            function evaluate(tokens: (boolean | string)[]): boolean {
                const stack: (boolean | string)[] = [];
                //console.log('evaluateReducedExpression', 'tokens', tokens);

                try {
                    tokens.forEach(token => {

                        if (typeof token === 'boolean') {
                            //if prior value is an operator, then evaluate and reduce expression
                            const prev = stack.pop();
                            if (typeof prev === 'string' && prev in operators) {
                                const leftOperand = stack.pop() as boolean;
                                stack.push(operators[prev](leftOperand, token));
                            } else {
                                //put the prev boolean back on the stack and add the current boolean
                                stack.push(prev);
                                stack.push(token);
                            }
                            
                        } else if (token in operators) {
                            stack.push(token); //push operator on stack - can't evaluate yet
                        } else if (token === '(') {
                            stack.push(token);
                        } else if (token === ')') {
                            //should only be 1 value?
                            const subStack: boolean[] = [];
                            while (stack.length > 0) {
                                const top = stack.pop();
                                if (top === '(') break;
                                subStack.unshift(top as boolean);
                            }

                            if (subStack.length === 1) {
                                const result = subStack.pop() as boolean;
                                stack.push(result);
                            } else {
                                //similar to a reducer, but wanted to use custom logic
                                let result = evaluateReducedExpressionNoParenthesis(subStack);
                                
                                stack.push(result);
                            }
                        }
                    });
        
                } catch (error) {
                    console.error('evaluateReducedExpression', 'error', stack, error);
                }


                //return true by default since that it means it wasn't false
                if (stack.length === 0) {
                    return true;
                } else if (stack.length === 1 && typeof stack[0] === 'boolean') {
                    return stack[0] as boolean;
                } else {
                    let runningResult: boolean = stack[0] as boolean;
                    for (let i = 0; i < stack.length; i += 1) {
                        const current = stack[i] as string;
                        if (current in operators) {
                            const currentOperator = current;
                            if (currentOperator === 'NOT') {
                                const nextValue = stack[i + 1] as boolean;
                                runningResult = !operators[currentOperator](nextValue);
                                i += 1; //skip next value as it has been processed
                            } else if (i > 0) {
                                const prevValue = stack[i - 1] as boolean;
                                const nextValue = stack[i + 1] as boolean;
                                runningResult = operators[currentOperator](runningResult, nextValue);
                                i += 1; //skip next value as it has been processed
                            } else {
                                //default to false - shbould never get here if current element is an operator
                                runningResult = false;
                            }
                        }
                    }
                    return runningResult;
                }

            }

            function evaluateReducedExpressionNoParenthesis(stack: (boolean | string)[]): boolean {
                let rtn: boolean = true;
                if (stack.length === 0) {
                    rtn = true;
                } else if (stack.length === 1 && typeof stack[0] === 'boolean') {
                    rtn = stack[0] as boolean;
                } else {
                    let runningResult: boolean = stack[0] as boolean;
                    for (let i = 0; i < stack.length; i += 1) {
                        const current = stack[i] as string;
                        if (current in operators) {
                            const currentOperator = current;
                            if (currentOperator === 'NOT') {
                                const nextValue = stack[i + 1] as boolean;
                                runningResult = !operators[currentOperator](nextValue);
                                i += 1; //skip next value as it has been processed
                            } else if (i > 0) {
                                const prevValue = stack[i - 1] as boolean;
                                const nextValue = stack[i + 1] as boolean;
                                runningResult = operators[currentOperator](runningResult, nextValue);
                                i += 1; //skip next value as it has been processed
                            } else {
                                //default to false - shbould never get here if current element is an operator
                                runningResult = false;
                            }
                        }
                    }
                    rtn = runningResult;                
                }
            
                return rtn;
            }



            
        
            return evaluate(tokens);
        }
    

        //add parentheses if not present - simplifies logic
        if (!expression.startsWith('(')) {
            expression = '(' + expression.trim() + ')';
        }
        //console.log('expression', expression);
        
        //gets individual tokens of clause - all tokens
        const tokens = expression.match(/(\(|\)|'[^']*'|\w+|!=|=|IN|NOT IN|AND|OR)/g) || []; //add |NOT if needed

        //consolidate NOT IN or IN into one token for one operator
        let tokensCleaned = [] as string[];
        for (let i = 0; i < tokens.length; i++) {
            if (tokens[i].includes('NOT') && tokens[i +1].includes("IN")) {
                tokensCleaned.push(tokens[i] + " " + tokens[i + 1]);
                i++;
            } else {
                tokensCleaned.push(tokens[i]);
            }
        }

        //console.log('evaluateBooleanExpression', 'tokensCleaned', tokensCleaned);

        let result = evaluate(tokensCleaned);

        return result;
    }

}
